import React, {useCallback, useEffect, useRef, useState} from "react"
import {Navigate, useNavigate} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {compose} from "../../utils/ImageUtil"
import {processAiEffect} from "../../library/requests/AiRequests"
import {useTranslation} from "react-i18next"
import {Alert, Stack} from "@mui/material"
import EditImagePreview from "../edit-tools/EditImagePreview"
import EditControls from "../edit-tools/EditControls"
import HiddenCanvas from "../common/HiddenCanvas"
import * as StickerUtil from "../../utils/StickerUtil"

function EditPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const config = useConfig()
  const {
    rawImage,
    aiProcessedImage,
    setCompositeImage,
    clearImages,
    aiImageHistory,
    selectedAiImageIndex,
    addAiImage,
    selectAiImage,
    guestInputValues,
  } = useBoothSession()

  const sharedCanvas = useRef<HTMLCanvasElement>(null)

  const [selectedBackground, setSelectedBackground] = useState<string | null>(null)
  const [selectedOverlay, setSelectedOverlay] = useState<string | null>(null)

  // Check if background removal was expected but failed
  const backgroundRemovalFailed = config.backgroundReplacementOn && !aiProcessedImage
  // Check if AI effect was expected but failed
  const aiEffectFailed = config.aiEffectOn && !aiProcessedImage
  const [imageError, setImageError] = useState<string | null>(() => {
    if (aiEffectFailed)
      return t("edit.errors.aiEffectFailed")
    if (backgroundRemovalFailed)
      return t("edit.errors.backgroundRemovalFailed")
    return null
  })

  const [aiEffectRegenerationsRemaining, setAiEffectRegenerationsRemaining] = useState(0)
  const [aiEffectProcessing, setAiEffectProcessing] = useState(false)

  useEffect(() => {
    if (config.aiEffectSequence) {
      setAiEffectRegenerationsRemaining(config.aiEffectSequence.maxNumberOfRegenerations)
    }
  }, [config.aiEffectSequence])

  const handleUpload = useCallback(() => {
    const userImg = document.querySelector("#userImage") as HTMLImageElement
    const overlayImg = document.querySelector("#overlayImage") as HTMLImageElement
    const stickers = Array.from(
      document.querySelectorAll("#stickersDropzone > .sticker"),
    ) as HTMLImageElement[]
    const backgroundImg = document.querySelector("#selectedBackground") as HTMLImageElement

    const canvas = sharedCanvas.current
    if (!canvas || !userImg) {
      console.error("Failed to compose image: missing canvas or user image")
      setImageError(t("edit.errors.composeFailed"))
      return
    }

    const composedImage = compose(userImg, overlayImg, backgroundImg, stickers, canvas, config.captureAspectRatio)
    setCompositeImage(composedImage)
    navigate(`/${config.presetCode}/wrap-up`)
  }, [config, navigate, setCompositeImage, t])

  const hasGuestInputs = (config.aiEffectSequence?._embedded.guestInputs ?? []).length > 0

  const resetBooth = useCallback(() => {
    clearImages()
    navigate(`/${config.presetCode}/camera`)
  }, [config, navigate, clearImages])

  const goBackToGuestInput = useCallback(() => {
    navigate(`/${config.presetCode}/guest-input`)
  }, [config, navigate])

  const handleAiEffectRegenerate = useCallback(async () => {
    if (!rawImage) return
    setAiEffectProcessing(true)
    try {
      const imageUrl = await processAiEffect(config.presetCode, rawImage, guestInputValues, config.captureAspectRatio)
      addAiImage(imageUrl)
      setAiEffectRegenerationsRemaining((prev) => prev - 1)
    } catch {
      // Don't decrement on failure
    }
    setAiEffectProcessing(false)
  }, [config, rawImage, addAiImage, guestInputValues])

  // Redirect to camera if no image
  if (!rawImage) {
    return (
      <Navigate
        to={`/${config.presetCode}/camera`}
        replace
      />
    )
  }

  const userImage = aiImageHistory[selectedAiImageIndex] ?? aiProcessedImage ?? rawImage

  // Filter out backgrounds tool if background removal failed
  const tools = backgroundRemovalFailed
    ? config.editTools.filter((tool) => tool !== "backgrounds")
    : config.editTools

  return (
    <>
      <HiddenCanvas ref={sharedCanvas}/>
      <section onMouseDown={StickerUtil.handleOutsideClick}>
        <Stack
          spacing={3}
          alignItems="center"
          justifyContent="stretch"
        >
          <EditImagePreview
            userImage={userImage}
            currentOverlayUrl={selectedOverlay}
            currentBackgroundUrl={selectedBackground}
          />

          {imageError && (
            <Alert
              severity="warning"
              onClose={() => setImageError(null)}
            >
              {imageError}
            </Alert>
          )}

          <EditControls
            tools={tools}
            rawImage={rawImage}
            userImage={userImage}
            onChangeBackground={setSelectedBackground}
            onChangeOverlay={setSelectedOverlay}
            onBack={hasGuestInputs ? goBackToGuestInput : undefined}
            onRetake={resetBooth}
            onSubmit={handleUpload}
            aiEffectRemainingRegenerations={aiEffectRegenerationsRemaining}
            aiEffectIsProcessing={aiEffectProcessing}
            onAiEffectRegenerate={handleAiEffectRegenerate}
            aiImageHistory={aiImageHistory}
            selectedAiImageIndex={selectedAiImageIndex}
            onAiImageSelect={selectAiImage}
          />
        </Stack>
      </section>
    </>
  )
}

export default EditPage
